/**
 * Feature Flags System
 * Provides runtime feature toggling for gradual rollouts and A/B testing
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export interface FeatureFlag {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  environment?: string;
  percentage?: number; // For gradual rollouts (0-100)
  user_groups?: string[]; // Specific user groups
  org_ids?: string[]; // Specific organizations
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface FeatureFlagEvaluation {
  enabled: boolean;
  source: 'default' | 'database' | 'override' | 'percentage' | 'group';
  flag: FeatureFlag;
}

class FeatureFlagService {
  private cache: Map<string, FeatureFlag> = new Map();
  private cacheExpiry: number = 5 * 60 * 1000; // 5 minutes
  private lastFetch: number = 0;
  private overrides: Map<string, boolean> = new Map();
  private userId?: string;
  private orgId?: string;
  private userGroups: string[] = [];

  /**
   * Initialize feature flags with user context
   */
  async initialize(userId?: string, orgId?: string, userGroups: string[] = []) {
    this.userId = userId;
    this.orgId = orgId;
    this.userGroups = userGroups;

    // Load overrides from localStorage (for testing)
    this.loadLocalOverrides();

    // Fetch flags from database
    await this.refreshFlags();

    // Log initialization (development only)
    if (import.meta.env.DEV) {
      logger.info('Feature flags initialized', {
        userId,
        orgId,
        flagCount: this.cache.size,
      });
    }
  }

  /**
   * Check if a feature is enabled
   */
  async isEnabled(flagId: string): Promise<boolean> {
    const evaluation = await this.evaluate(flagId);
    return evaluation.enabled;
  }

  /**
   * Evaluate a feature flag with full context
   */
  async evaluate(flagId: string): Promise<FeatureFlagEvaluation> {
    // Check for local override first
    if (this.overrides.has(flagId)) {
      return {
        enabled: this.overrides.get(flagId)!,
        source: 'override',
        flag: {
          id: flagId,
          name: flagId,
          enabled: this.overrides.get(flagId)!,
        },
      };
    }

    // Refresh cache if expired
    if (this.isCacheExpired()) {
      await this.refreshFlags();
    }

    // Get flag from cache
    const flag = this.cache.get(flagId);

    if (!flag) {
      // Flag not found, default to disabled
      return {
        enabled: false,
        source: 'default',
        flag: {
          id: flagId,
          name: flagId,
          enabled: false,
        },
      };
    }

    // Check environment
    if (flag.environment && flag.environment !== import.meta.env.MODE) {
      return {
        enabled: false,
        source: 'database',
        flag,
      };
    }

    // Check organization-specific flags
    if (flag.org_ids && flag.org_ids.length > 0) {
      if (!this.orgId || !flag.org_ids.includes(this.orgId)) {
        return {
          enabled: false,
          source: 'database',
          flag,
        };
      }
    }

    // Check user group restrictions
    if (flag.user_groups && flag.user_groups.length > 0) {
      const hasGroup = flag.user_groups.some(group => this.userGroups.includes(group));

      if (!hasGroup) {
        return {
          enabled: false,
          source: 'group',
          flag,
        };
      }
    }

    // Check percentage rollout
    if (flag.percentage !== undefined && flag.percentage !== null) {
      const enabled = this.isInPercentage(flagId, flag.percentage);
      return {
        enabled,
        source: 'percentage',
        flag,
      };
    }

    // Return flag status
    return {
      enabled: flag.enabled,
      source: 'database',
      flag,
    };
  }

  /**
   * Get all feature flags
   */
  async getAllFlags(): Promise<FeatureFlag[]> {
    if (this.isCacheExpired()) {
      await this.refreshFlags();
    }
    return Array.from(this.cache.values());
  }

  /**
   * Get flags for specific context
   */
  async getFlagsForContext(): Promise<Record<string, boolean>> {
    const flags = await this.getAllFlags();
    const result: Record<string, boolean> = {};

    for (const flag of flags) {
      const evaluation = await this.evaluate(flag.id);
      result[flag.id] = evaluation.enabled;
    }

    return result;
  }

  /**
   * Set a local override for testing
   */
  setOverride(flagId: string, enabled: boolean) {
    this.overrides.set(flagId, enabled);
    this.saveLocalOverrides();

    logger.info('Feature flag override set', { flagId, enabled });
  }

  /**
   * Clear a local override
   */
  clearOverride(flagId: string) {
    this.overrides.delete(flagId);
    this.saveLocalOverrides();

    logger.info('Feature flag override cleared', { flagId });
  }

  /**
   * Clear all local overrides
   */
  clearAllOverrides() {
    this.overrides.clear();
    localStorage.removeItem('featureFlagOverrides');

    logger.info('All feature flag overrides cleared');
  }

  /**
   * Refresh flags from database
   */
  private async refreshFlags() {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase.from('feature_flags').select('*').eq('active', true);

      if (error) {
        logger.error('Failed to fetch feature flags', error);
        return;
      }

      // Update cache
      this.cache.clear();

      if (data) {
        for (const flag of data) {
          this.cache.set(flag.id, flag);
        }
      }

      this.lastFetch = Date.now();

      logger.debug('Feature flags refreshed', { count: this.cache.size });
    } catch (error) {
      logger.error('Error refreshing feature flags', error);
    }
  }

  /**
   * Check if cache is expired
   */
  private isCacheExpired(): boolean {
    return Date.now() - this.lastFetch > this.cacheExpiry;
  }

  /**
   * Check if user is in percentage rollout
   */
  private isInPercentage(flagId: string, percentage: number): boolean {
    if (!this.userId) {
      // No user context, use random
      return Math.random() * 100 < percentage;
    }

    // Use consistent hashing for user
    const hash = this.hashString(`${flagId}:${this.userId}`);
    const bucket = hash % 100;

    return bucket < percentage;
  }

  /**
   * Simple string hash function
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Load local overrides from localStorage
   */
  private loadLocalOverrides() {
    try {
      const stored = localStorage.getItem('featureFlagOverrides');
      if (stored) {
        const overrides = JSON.parse(stored);
        this.overrides = new Map(Object.entries(overrides));
      }
    } catch (error) {
      logger.error('Failed to load feature flag overrides', error);
    }
  }

  /**
   * Save local overrides to localStorage
   */
  private saveLocalOverrides() {
    try {
      const overrides = Object.fromEntries(this.overrides);
      localStorage.setItem('featureFlagOverrides', JSON.stringify(overrides));
    } catch (error) {
      logger.error('Failed to save feature flag overrides', error);
    }
  }

  /**
   * Subscribe to real-time flag updates
   */
  subscribeToUpdates(callback: (flag: FeatureFlag) => void) {
    let active = true;
    let supabaseClient: any = null;
    let channel: any = null;

    const setupSubscription = async () => {
      try {
        supabaseClient = await getAuthenticatedSupabaseClient();
        if (!active) {
          return;
        }

        channel = supabaseClient
          .channel('feature-flags')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'feature_flags',
            },
            async payload => {
              logger.debug('Feature flag update received', payload);
              await this.refreshFlags();
              if (payload.new) {
                callback(payload.new as FeatureFlag);
              }
            },
          )
          .subscribe();
      } catch (error) {
        logger.error('Failed to subscribe to feature flag updates', error);
      }
    };

    setupSubscription();

    return () => {
      active = false;
      if (channel && supabaseClient) {
        supabaseClient.removeChannel(channel);
      }
    };
  }
}

// Singleton instance
export const featureFlags = new FeatureFlagService();

// React hook for feature flags
export function useFeatureFlag(flagId: string): {
  enabled: boolean;
  loading: boolean;
  evaluation?: FeatureFlagEvaluation;
} {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [evaluation, setEvaluation] = useState<FeatureFlagEvaluation>();

  useEffect(() => {
    let mounted = true;

    const checkFlag = async () => {
      setLoading(true);
      try {
        const evaluation = await featureFlags.evaluate(flagId);
        if (mounted) {
          setEnabled(evaluation.enabled);
          setEvaluation(evaluation);
        }
      } catch (error) {
        logger.error('Failed to evaluate feature flag', { flagId, error });
        if (mounted) {
          setEnabled(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    checkFlag();

    // Subscribe to updates
    const unsubscribe = featureFlags.subscribeToUpdates(flag => {
      if (flag.id === flagId) {
        checkFlag();
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [flagId]);

  return { enabled, loading, evaluation };
}

// Feature flag IDs (constants for type safety)
export const FEATURE_FLAGS = {
  // UI Features
  NEW_FLOOR_PLAN_VIEWER: 'new_floor_plan_viewer',
  ADVANCED_ANNOTATIONS: 'advanced_annotations',
  REAL_TIME_COLLABORATION: 'real_time_collaboration',
  DARK_MODE: 'dark_mode',

  // Product Features
  PORTAL_IO_INTEGRATION: 'portal_io_integration',
  AI_PRODUCT_SEARCH: 'ai_product_search',
  CAD_FILE_PROCESSING: 'cad_file_processing',
  ROOM_DETECTION_AI: 'room_detection_ai',

  // Performance Features
  LAZY_LOADING: 'lazy_loading',
  OPTIMISTIC_UPDATES: 'optimistic_updates',
  BACKGROUND_SYNC: 'background_sync',

  // Experimental Features
  BETA_FEATURES: 'beta_features',
  DEBUG_MODE: 'debug_mode',
  PERFORMANCE_MONITORING: 'performance_monitoring',

  // Security Features
  TWO_FACTOR_AUTH: 'two_factor_auth',
  AUDIT_LOGGING: 'audit_logging',
  DATA_ENCRYPTION: 'data_encryption',
} as const;

// Default feature flag configuration
export const DEFAULT_FLAGS: FeatureFlag[] = [
  {
    id: FEATURE_FLAGS.NEW_FLOOR_PLAN_VIEWER,
    name: 'New Floor Plan Viewer',
    description: 'Enhanced floor plan viewer with better performance',
    enabled: true,
    percentage: 100,
  },
  {
    id: FEATURE_FLAGS.PORTAL_IO_INTEGRATION,
    name: 'Portal.io Integration',
    description: 'Enable Portal.io product import and sync',
    enabled: true,
  },
  {
    id: FEATURE_FLAGS.AI_PRODUCT_SEARCH,
    name: 'AI Product Search',
    description: 'AI-powered product search using Groq/OpenAI',
    enabled: true,
  },
  {
    id: FEATURE_FLAGS.REAL_TIME_COLLABORATION,
    name: 'Real-time Collaboration',
    description: 'Enable real-time collaboration features',
    enabled: true,
    percentage: 50, // Gradual rollout
  },
  {
    id: FEATURE_FLAGS.DARK_MODE,
    name: 'Dark Mode',
    description: 'Enable dark mode UI option',
    enabled: false, // Coming soon
  },
  {
    id: FEATURE_FLAGS.BETA_FEATURES,
    name: 'Beta Features',
    description: 'Enable beta features for testing',
    enabled: false,
    user_groups: ['beta_testers', 'internal'],
  },
  {
    id: FEATURE_FLAGS.DEBUG_MODE,
    name: 'Debug Mode',
    description: 'Enable debug logging and tools',
    enabled: true,
    environment: 'development',
  },
];

import { useState, useEffect } from 'react';
